// Fake a slow connection on a local dev socket, so "how does this look at 90ms?" is a question
// you can answer at your desk instead of guessing.
//
// Everything about how other players look -- interpolation, the size of the update gaps a client
// has to bridge, reconciliation on the local player -- only misbehaves once frames stop arriving
// the instant they are sent. On localhost they always do, which is exactly the case that hides
// every latency bug. The headless harness conditions its loopback the same way (see
// __tests__/integration/loopback.ts) and is the right place to MEASURE; this is for looking.
//
// Only installed against a local server, and idle until somebody asks for latency:
//
//     netsim.set({ rttMs: 90, jitterMs: 20 })   // both directions, so 90ms round trip
//     netsim.off()
//
// Delay never reorders, because a WebSocket runs over TCP and neither would a real one.

export interface NetworkConditionerSettings {
    /** Round-trip time to simulate; half of it is applied to each direction. */
    rttMs: number
    /** Random +/- ms on top of the one-way delay, per frame. Bunches frames; never reorders. */
    jitterMs: number
}

const settings: NetworkConditionerSettings = { rttMs: 0, jitterMs: 0 }

/** Last scheduled delivery time per direction, so a jittered frame never overtakes its predecessor. */
const watermark = { inbound: 0, outbound: 0 }

const oneWayDelay = () => {
    const base = settings.rttMs / 2
    if (base <= 0 && settings.jitterMs <= 0) {
        return 0
    }
    const jitter = settings.jitterMs > 0 ? (Math.random() * 2 - 1) * settings.jitterMs : 0
    return Math.max(0, base + jitter)
}

const schedule = (direction: "inbound" | "outbound", deliver: () => void) => {
    const delay = oneWayDelay()
    if (delay <= 0) {
        deliver()
        return
    }
    const now = Date.now()
    const at = Math.max(now + delay, watermark[direction])
    watermark[direction] = at
    setTimeout(deliver, at - now)
}

/**
 * Wrap a live socket so both directions can be delayed. Takes over the socket's `onmessage`
 * property: the client assigns its handler as usual, we hold the frame for the configured delay
 * and then call it. With no delay configured this is a straight pass-through.
 */
export const installNetworkConditioner = (ws: WebSocket) => {
    let handler: ((event: { data: string }) => void) | null = null
    Object.defineProperty(ws, "onmessage", {
        configurable: true,
        get: () => handler,
        set: (fn: any) => {
            handler = fn
        },
    })
    ws.addEventListener("message", event => {
        schedule("inbound", () => handler?.(event as any))
    })

    const send = ws.send.bind(ws)
    ws.send = (data: any) =>
        schedule("outbound", () => {
            // A held frame can come due after the socket has closed, which the real send()
            // answers with a throw. Nothing upstream expects that from a send it made a moment
            // ago, and the frame is moot either way.
            if (ws.readyState !== WebSocket.OPEN) {
                return
            }
            send(data)
        })

    const api = {
        set: (next: Partial<NetworkConditionerSettings>) => {
            Object.assign(settings, next)
            return { ...settings }
        },
        off: () => {
            settings.rttMs = 0
            settings.jitterMs = 0
            return { ...settings }
        },
        get: () => ({ ...settings }),
    }
    ;(window as any).netsim = api
    return api
}
